import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useCallback, useMemo, useState } from 'react';

import {
  type EventPaymentHistoryInvoice,
  useGetEventPaymentHistory,
} from '~entities/events/api/get-event-payment-history';
import { useCurrentStudio } from '~entities/studios/model/studio-store';

import { useGetCurrency } from '~shared/api/currency-api';
import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { getStoredPageSize } from '~shared/helpers/page-size-preference';
import { cn } from '~shared/shadcn/lib/utils';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';

const columnHelper = createColumnHelper<EventPaymentHistoryInvoice>();

interface EventPaymentHistoryTableProps {
  eventId: string;
}

export const EventPaymentHistoryTable = ({
  eventId,
}: EventPaymentHistoryTableProps) => {
  const currentStudio = useCurrentStudio();
  const studioTimezone = currentStudio?.timezone;
  const { data: currencyData } = useGetCurrency();

  const formatDateForDisplay = useCallback(
    (iso: string, fmt: string) => {
      try {
        const date = parseISO(iso);
        if (studioTimezone) {
          return formatInTimeZone(date, studioTimezone, fmt);
        }
        return format(date, fmt);
      } catch {
        return '';
      }
    },
    [studioTimezone],
  );

  const formatCurrency = useMemo(() => {
    const currency = currencyData?.name ?? 'USD';
    const symbol = currencyData?.symbol ?? '$';

    return (amount: number) => {
      if (symbol && symbol !== currency) {
        return `${symbol}${amount.toFixed(2)}`;
      }

      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
      }).format(amount);
    };
  }, [currencyData?.name, currencyData?.symbol]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('studentName', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Student Name'
            disableSorting
          />
        ),
        cell: (info) => {
          const studentId = info.row.original.studentId;
          if (!studentId) {
            return <div className='px-4 py-2'>{info.getValue()}</div>;
          }
          return (
            <Button
              variant='link'
              className='px-4 py-2 text-custom-secondary-1'
              asChild
            >
              <Link to='/students/$studentId/details' params={{ studentId }}>
                {info.getValue()}
              </Link>
            </Button>
          );
        },
      }),
      columnHelper.accessor('paymentStatus', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Status'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2'>
            <Badge
              className={cn({
                'bg-green-100 text-green-800': info.getValue() === 'succeeded',
                'bg-destructive text-destructive-foreground':
                  info.getValue() === 'failed',
                'bg-yellow-100 text-yellow-800':
                  info.getValue() === 'pending' ||
                  info.getValue() === 'partial refund',
                'bg-blue-100 text-blue-800': info.getValue() === 'full refund',
              })}
              variant='default'
            >
              {info.getValue()}
            </Badge>
          </div>
        ),
      }),
      columnHelper.accessor('enrollmentStatus', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Enrollment Status'
            disableSorting
          />
        ),
        cell: (info) => <div className='px-4 py-2'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('enrollmentDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Enrollment Date'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {value ? formatDateForDisplay(value, 'MMM, dd, yyyy') : '—'}
            </div>
          );
        },
      }),
      columnHelper.accessor('dueDate', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Due Date'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {value ? formatDateForDisplay(value, 'MMM, dd, yyyy') : '—'}
            </div>
          );
        },
      }),
      columnHelper.accessor('totalAmount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Total Invoice'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {typeof value === 'number' ? formatCurrency(value) : '—'}
            </div>
          );
        },
      }),
      columnHelper.accessor('amount', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Amount Paid'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              {typeof value === 'number' ? formatCurrency(value) : '—'}
            </div>
          );
        },
      }),
      columnHelper.accessor('paymentIntentId', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Payment Intent ID'
            disableSorting
          />
        ),
        cell: (info) => (
          <div className='px-4 py-2 font-mono text-xs'>
            {info.getValue() ?? '—'}
          </div>
        ),
      }),
      columnHelper.accessor('problem', {
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Problem'
            disableSorting
          />
        ),
        cell: (info) => {
          const value = info.getValue();
          return (
            <div className='px-4 py-2'>
              <Badge
                className={cn({
                  'bg-[#D4EDDA]': value === 'OK',
                  'bg-[#F8D7DA]': value !== 'OK',
                })}
                variant='default'
              >
                {value}
              </Badge>
            </div>
          );
        },
      }),
    ],
    [formatCurrency, formatDateForDisplay],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: getStoredPageSize(),
  });

  const { data, isFetching } = useGetEventPaymentHistory({
    eventId,
  });

  const sortedData = useMemo(() => {
    if (!data) return [];
    return [...data].sort((a, b) => {
      if (a.problem !== 'OK' && b.problem === 'OK') return -1;
      if (a.problem === 'OK' && b.problem !== 'OK') return 1;
      return a.studentName.localeCompare(b.studentName);
    });
  }, [data]);

  return (
    <div className='flex flex-col gap-2'>
      <EnrollioTable
        ariaLabel='Event payment history'
        totalLabel='Payments'
        filterHeader={
          <span className='text-sm text-muted-foreground'>
            Payments requiring attention appear first
          </span>
        }
        toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
        columnFilters={columnFilters}
        columnVisibility={columnVisibility}
        columns={columns}
        data={sortedData}
        enableRowSelection
        getRowId={(row) => row._id}
        onColumnFiltersChange={setColumnFilters}
        onColumnVisibilityChange={setColumnVisibility}
        onPaginationChange={setPagination}
        onRowSelectionChange={setRowSelection}
        onSortingChange={setSorting}
        pagination={pagination}
        isUpdating={isFetching}
        rowSelection={rowSelection}
        sorting={sorting}
        showSelectionCount
      />
    </div>
  );
};
